/**
 * UserAvatar Component
 * 
 * Displays a user's avatar image with fallback to initials.
 * Handles loading states and error scenarios gracefully.
 */

import React, { useState } from 'react';
import { css } from '@emotion/css';
import Text from '@amzn/meridian/text';

interface UserAvatarProps {
  userAlias: string | null;
  avatarUrl: string | null;
  size?: number;
  showOnError?: boolean;
}

const getUserInitials = (userAlias: string | null): string => {
  if (!userAlias) return 'U';
  
  const parts = userAlias.split(/[\s._-]+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return userAlias.charAt(0).toUpperCase();
};

export const UserAvatar: React.FC<UserAvatarProps> = ({ 
  userAlias, 
  avatarUrl, 
  size = 32,
  showOnError = true 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const avatarStyles = css({
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E3E7EA', // Neutral background color
    border: '1px solid #D4D8DC',
    flexShrink: 0
  });

  const imageStyles = css({
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  });

  const initialsStyles = css({
    fontSize: `${Math.floor(size * 0.4)}px`,
    fontWeight: 'bold',
    color: '#555',
    userSelect: 'none'
  });

  // Show initials if no avatar URL, image failed to load, or we're on localhost
  const shouldShowInitials = !avatarUrl || imageError || !showOnError;

  return (
    <div className={avatarStyles}>
      {!shouldShowInitials && avatarUrl ? (
        <img
          src={avatarUrl}
          alt={`${userAlias}'s avatar`}
          className={imageStyles}
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ 
            display: imageError ? 'none' : 'block'
          }}
        />
      ) : (
        <div className={initialsStyles}>
          {getUserInitials(userAlias)}
        </div>
      )}
    </div>
  );
};
